import type { AdminMemberView, UserRole, UserStatus } from "@seedance/contracts";
import { ApiClientError } from "../api/client.js";

export interface MemberDraft {
  action: "create" | "set_role" | "set_status" | "reset_password";
  username: string;
  role: UserRole;
  status: UserStatus;
  password?: string;
  target?: AdminMemberView;
  commandId: string;
}
export interface MemberActionState {
  status: "idle" | "editing" | "submitting" | "succeeded" | "conflict" | "unknown" | "error";
  draft?: MemberDraft;
  message?: string;
}
type Event = { type: "open"; draft: MemberDraft } | { type: "edit"; changes: Partial<MemberDraft> } | { type: "submit" } | { type: "success" } | { type: "failure"; error: unknown } | { type: "dismiss" } | { type: "refresh"; target: AdminMemberView; commandId: string };

export function memberActionReducer(state: MemberActionState, event: Event): MemberActionState {
  switch (event.type) {
    case "open": return { status: "editing", draft: event.draft };
    case "dismiss": return { status: "idle" };
    case "edit": return state.draft && ["editing", "error", "conflict"].includes(state.status) ? { ...state, draft: { ...state.draft, ...event.changes } } : state;
    case "submit": return state.draft && ["editing", "error"].includes(state.status) ? { status: "submitting", draft: state.draft } : state;
    case "success": return { status: "succeeded", message: "操作已完成。临时密码已从本页清除。" };
    case "refresh": return state.draft && state.status === "conflict" ? { status: "editing", draft: { ...state.draft, target: event.target, commandId: event.commandId } } : state;
    case "failure": {
      const error = event.error;
      if (!(error instanceof ApiClientError) || error.status >= 500) return { ...state, status: "unknown", message: "结果不确定，服务端可能已完成操作。不会自动重试；请先核对成员列表。" };
      if (error.code === "MEMBER_VERSION_CONFLICT") return { ...state, status: "conflict", message: "此成员已被修改，输入已保留。请刷新版本并重新确认，不会覆盖别人的更改。" };
      const messages: Record<string, string> = {
        LAST_ACTIVE_SYSTEM_ADMINISTRATOR: "至少保留一名启用的管理员，不能执行此操作。",
        ACTOR_NOT_AUTHORIZED: "你的管理权限已失效，请重新登录。",
        USERNAME_ALREADY_EXISTS: "用户名已存在，请换一个用户名或核对已有成员。",
        INVALID_CSRF_TOKEN: "会话校验已失效，请刷新页面后重新操作。",
        COMMAND_CONFLICT: "操作编号冲突，请关闭窗口并重新选择操作。"
      };
      return { ...state, status: "error", message: messages[error.code] ?? "操作未完成，请核对输入和权限后重试。" };
    }
  }
}

export function createTemporaryPassword(): string {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";
  return Array.from(crypto.getRandomValues(new Uint8Array(20)), byte => alphabet[byte & 63]).join("");
}
